import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { getVisualAssetManifest, updateVisualPreloadSummary, VisualAssetEntry } from '../data/assets/visualAssets';
import { createLogger } from '../utils/Logger';

export class Preload extends Phaser.Scene {
  private static readonly OPTIONAL_COMBAT_SFX_KEYS = Object.freeze([
    'hit', 'crit', 'burn', 'slow', 'chain', 'area_hit',
    'enemy_death', 'boss_death', 'player_hit', 'boss_warning',
    'levelup', 'chest_open', 'evolution', 'victory', 'game_over'
  ]);
  private readonly logger = createLogger('Preload');
  constructor() {
    super('Preload');
  }

  preload() {
    this.logger.debug('preload:start');
    const isE2E = new URLSearchParams(window.location.search).has('e2e');
    const visualAssets = getVisualAssetManifest(this.logger);
    const queuedKeys: string[] = [];
    const loadedKeys: string[] = [];
    const failedKeys: string[] = [];
    const requiredFailures: string[] = [];
    const skippedOptionalKeys = visualAssets
      .filter(asset => !asset.url && !asset.required)
      .map(asset => asset.key);

    if (!isE2E) {
      this.load.audio('menu_theme', 'assets/audio/menu_theme.mp3');
      this.load.audio('level_theme', 'assets/audio/level_theme.mp3');
    }
    this.logger.info('optional-combat-sfx:configured', {
      optionalCombatSfx: Preload.OPTIONAL_COMBAT_SFX_KEYS
    });

    this.queueVisualAssets(visualAssets, queuedKeys);
    this.logger.info('visual-assets:batch-queued', {
      queuedKeys,
      skippedOptionalKeys
    });

    // Create a simple loading bar
    const width = this.scale.width;
    const height = this.scale.height;
    
    let loadingText = this.add.text(width / 2, height / 2 - 50, LocalizationManager.getInstance().getText('ui.loading'), {
        font: '20px monospace',
        color: '#ffffff'
    }).setOrigin(0.5, 0.5);

    this.load.on('filecomplete', (key: string) => {
      if (queuedKeys.includes(key)) {
        loadedKeys.push(key);
      }
    });
    this.load.on('loaderror', (file: { key?: string }) => {
      const key = String(file?.key ?? '');
      if (!key) return;
      failedKeys.push(key);
      const asset = visualAssets.find(entry => entry.key === key);
      const payload = {
        key,
        type: asset?.type,
        path: asset?.url,
        required: asset?.required ?? false
      };
      if (asset?.required) {
        requiredFailures.push(key);
        this.logger.error('visual-assets:load-error-required', payload);
      } else {
        this.logger.warn('visual-assets:load-error-optional', payload);
      }
    });
    this.load.on('complete', () => {
        updateVisualPreloadSummary({
          manifestKeys: visualAssets.map(asset => asset.key),
          queuedKeys,
          loadedKeys,
          skippedOptionalKeys,
          failedKeys,
          requiredFailures
        });
        this.logger.info('visual-assets:load-complete', {
          queued: queuedKeys.length,
          loaded: loadedKeys.length,
          failed: failedKeys.length,
          requiredFailures
        });
        loadingText.destroy();
    });
  }

  private queueVisualAssets(assets: readonly VisualAssetEntry[], queuedKeys: string[]): void {
    for (const asset of assets) {
      if (!asset.url) {
        if (!asset.required) {
          this.logger.warn('visual-assets:optional-skip-no-runtime-url', {
            key: asset.key,
            type: asset.type,
            required: asset.required
          });
        } else {
          this.logger.error('visual-assets:malformed-required-no-url', {
            key: asset.key,
            type: asset.type
          });
        }
        continue;
      }

      if (this.textures.exists(asset.key)) {
        this.logger.debug('visual-assets:skip-existing-texture', {
          key: asset.key,
          type: asset.type,
          path: asset.url,
          required: asset.required
        });
        continue;
      }

      this.logger.debug('visual-assets:queue', {
        key: asset.key,
        type: asset.type,
        path: asset.url,
        required: asset.required
      });
      if (asset.type === 'spritesheet') {
        if (!asset.frameConfig) {
          this.logger.error('visual-assets:malformed-spritesheet', { key: asset.key, path: asset.url });
          continue;
        }
        this.load.spritesheet(asset.key, asset.url, asset.frameConfig);
      } else if (asset.type === 'atlas') {
        this.logger.error('visual-assets:atlas-entry-missing-loader-metadata', { key: asset.key, path: asset.url });
        continue;
      } else {
        this.load.image(asset.key, asset.url);
      }
      queuedKeys.push(asset.key);
    }
  }

  create() {
    if (document.fonts) {
      const fonts = ['Rubik Mono One', 'Golos Text'];
      void Promise.all(fonts.map(family => new Promise<void>(resolve => {
        let timedOut = false;
        const timer = window.setTimeout(() => {
          timedOut = true;
          this.logger.warn('font:load-timeout', { family });
          resolve();
        }, 3000);
        document.fonts.load(`20px "${family}"`, 'Лягушнобль Frognobyl 0123456789').catch(() => {
          if (!timedOut) this.logger.warn('font:load-failed', { family });
        }).finally(() => {
          window.clearTimeout(timer);
          resolve();
        });
      }))).then(() => this.scene.start('MainMenu'));
      return;
    }
    this.scene.start('MainMenu');
  }
}
